import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { useCompaniesQuery } from '@/entities/company';
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '@/shared/utils';

type CompanyProviderState = {
  currentCompanyId: string | null;
  setCurrentCompanyId: (companyId: string | null) => Promise<void>;
  isUpdating: boolean;
  /** Компания загружается (инициализация localStorage + валидация по /companies) */
  isCompanyLoading: boolean;
};

const CompanyContext = createContext<CompanyProviderState | null>(null);

const STORAGE_KEY = 'protocolist-current-company-id';
const COMPANY_SCOPED_QUERY_KEYS = [
  ['meeting-groups'],
  ['workspace'],
  ['conversation', 'active'],
  ['conversation', 'active-meet-rooms-no-conversation'],
] as const;

const readStoredCompanyId = (): string | null => {
  const value = getLocalStorageItem(STORAGE_KEY);
  return value || null;
};

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const {
    data: companiesData,
    isPending: isCompaniesPending,
    isFetching: isCompaniesFetching,
  } = useCompaniesQuery();

  const [currentCompanyId, setLocalCompanyId] = useState<string | null>(() => {
    return readStoredCompanyId();
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const isCompanyLoading = isCompaniesPending || isCompaniesFetching;

  useEffect(() => {
    if (isCompaniesPending || isCompaniesFetching || !currentCompanyId) {
      return;
    }

    const companies = companiesData?.items ?? [];
    const hasAccess = companies.some((company) => company.id === currentCompanyId);

    if (!hasAccess) {
      setLocalCompanyId(null);
      removeLocalStorageItem(STORAGE_KEY);
    }
  }, [companiesData?.items, currentCompanyId, isCompaniesFetching, isCompaniesPending]);

  const setCurrentCompanyId = useCallback(
    async (companyId: string | null) => {
      if (currentCompanyId === companyId) {
        return;
      }

      setIsUpdating(true);
      try {
        setLocalCompanyId(companyId);

        if (companyId) {
          setLocalStorageItem(STORAGE_KEY, companyId);
        } else {
          removeLocalStorageItem(STORAGE_KEY);
        }

        await Promise.all(
          COMPANY_SCOPED_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [currentCompanyId, queryClient],
  );

  const value: CompanyProviderState = {
    currentCompanyId,
    setCurrentCompanyId,
    isUpdating,
    isCompanyLoading,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

export function useCurrentCompany(): CompanyProviderState {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error('useCurrentCompany must be used within CompanyProvider');
  }
  return ctx;
}
